"use client";

import { useRef } from "react";
import {
  mergeProps,
  useFocusRing,
  useTableCell,
  useTableColumnHeader,
  useTableHeaderRow,
  useTableRow,
  useTableRowGroup,
} from "react-aria";
import { csx } from "../utils";

export function TableHeaderRow({ item, state, children }) {
  let ref = useRef();
  let { rowProps } = useTableHeaderRow({ node: item }, state, ref);

  return (
    <tr className={csx("proton-Table__headerRow")} {...rowProps} ref={ref}>
      {children}
    </tr>
  );
}

type ColumnHeaderProps = {
  column: any;
  state: any;
};

export function TableColumnHeader({ column, state }: ColumnHeaderProps) {
  let ref = useRef();
  let { columnHeaderProps } = useTableColumnHeader(
    { node: column },
    state,
    ref
  );
  const { align = "left" } = column.props;
  let { isFocusVisible, focusProps } = useFocusRing();
  let arrowIcon = state.sortDescriptor?.direction === "ascending" ? "▲" : "▼";

  return (
    <th
      {...mergeProps(columnHeaderProps, focusProps)}
      colSpan={column.colspan}
      className={csx(
        "proton-Table__header",
        `proton-Table__header--${align}`,
        isFocusVisible ? "proton-Table__header--focused" : null
      )}
      ref={ref}
    >
      {column.rendered}
      {column.props.allowsSorting && (
        <span
          aria-hidden="true"
          className={csx("proton-Table__headerSortIcon")}
          style={{
            visibility:
              state.sortDescriptor?.column === column.key
                ? "visible"
                : "hidden",
          }}
        >
          {arrowIcon}
        </span>
      )}
    </th>
  );
}

/**
 * TableRowGroup groups the rows in the table header and table body
 */

type TableRowGroupProps = {
  type: "thead" | "tbody";
  children: React.ReactNode;
  isHidden?: boolean;
};

export function TableRowGroup({
  type: Element,
  children,
  isHidden = false,
}: TableRowGroupProps) {
  let { rowGroupProps } = useTableRowGroup();

  return (
    <Element
      {...rowGroupProps}
      className={csx(
        "proton-Table__rowGroup",
        Element === "thead"
          ? "proton-Table__rowGroup--header"
          : "proton-Table__rowGroup--body"
      )}
      style={isHidden ? { display: "none" } : undefined}
    >
      {children}
    </Element>
  );
}

export function TableRow({
  item,
  children,
  state,
  backgroundColor,
  onClick,
  showLines = true,
}) {
  let ref = useRef();
  let isSelected = state.selectionManager.isSelected(item.key);
  let { rowProps, isPressed } = useTableRow(
    {
      node: item,
    },
    state,
    ref
  );
  let { isFocusVisible, focusProps } = useFocusRing();

  return (
    <tr
      className={csx(
        "proton-Table__row",
        isSelected ? "proton-Table__row--selected" : null,
        isPressed ? "proton-Table__row--pressed" : null,
        isFocusVisible ? "proton-Table__row--focused" : null,
        showLines && "proton-Table__row--showLines",
        onClick && "proton-Table--clickable"
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
      {...mergeProps(rowProps, focusProps)}
      ref={ref}
      onClick={onClick ? () => onClick(item.key) : undefined}
    >
      {children}
    </tr>
  );
}

type CellProps = {
  cell: any;
  state: any;
  width?: number | string;
  onClick?: (key: string) => void;
};

export function TableCell({ cell, state, width, onClick }: CellProps) {
  let ref = useRef();
  let { gridCellProps } = useTableCell({ node: cell }, state, ref);
  let { isFocusVisible, focusProps } = useFocusRing();
  const { align = "left" } = cell.props;

  return (
    <td
      {...mergeProps(gridCellProps, focusProps)}
      className={csx(
        "proton-Table__cell",
        isFocusVisible ? "proton-Table__cell--focused" : null,
        `proton-Table__cell--${align}`,
        onClick && "proton-Table--clickable"
      )}
      style={width ? { width: width } : undefined}
      ref={ref}
      onClick={onClick ? () => onClick(cell.key) : undefined}
    >
      {cell.rendered}
    </td>
  );
}
